import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Heading,
  Stack,
  Frame,
  Icon,
  VideoThumbnail,
} from '@shopify/polaris';
import { CircleTickMajor, PlayMajor } from '@shopify/polaris-icons';
import LandingNav from './components/LandingNav';

const HowItWorks = () => {
  const navigate = useNavigate();
  
  const handleNavClick = (path) => {
    navigate(path);
  };
  
  const handleLoginClick = () => {
    navigate('/auth');
  };

  // Workflow steps data
  const workflowSteps = [
    {
      number: 1,
      title: 'Select Your Products',
      description: 'Choose individual products or select in bulk from your Shopify store.',
      icon: '🛍️',
      details: [
        'Filter products by title, collection, and tags',
        'Select all products or specific ones',
        'Preview product details before editing'
      ]
    },
    {
      number: 2,
      title: 'Choose Your Tools',
      description: 'Select from powerful tools for pricing, inventory, or product data.',
      icon: '🔧',
      details: [
        'Price adjustments with flexible options',
        'Inventory management tools',
        'Product data tools including SKU generator',
        'AI template system for content generation'
      ]
    },
    {
      number: 3,
      title: 'AI-Powered Content',
      description: 'Create engaging product content with customizable AI templates.',
      icon: '🤖',
      details: [
        'Choose from text or image-analyzing templates',
        'Complete freedom to customize your prompts',
        'Select multiple AI engines based on your needs',
        'Preview generated content before publishing'
      ]
    },
    {
      number: 4,
      title: 'Review & Push',
      description: 'Preview all changes and push them to your Shopify store instantly.',
      icon: '🚀',
      details: [
        'Review generated content before publishing',
        'Make final adjustments if needed',
        'Push changes with one click',
        'Automatic sync with your Shopify store'
      ]
    }
  ];

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/how-it-works" onNavClick={handleNavClick} />
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)',
          padding: '40px 20px',
          borderRadius: '8px',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading element="h1">
              <span style={{ fontSize: '2.2rem', fontWeight: '700', color: '#212B36' }}>
                How Editify Works
              </span>
            </Heading>
            <div style={{ margin: '20px 0', fontSize: '1.1rem', color: '#637381' }}>
              <p>See how Editify streamlines your Shopify product management workflow with powerful AI tools.</p>
            </div>
          </div>
        </div>

        {/* Video Demo Section */}
        <Card sectioned>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Heading>Watch the Demo</Heading>
            <Text variant="bodyMd" as="p" color="subdued" style={{ margin: '10px auto 30px', maxWidth: '600px' }}>
              See how Editify transforms your Shopify product management workflow
            </Text>
            
            <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
              <div style={{
                background: '#f4f6f8',
                borderRadius: '8px',
                height: '450px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #dfe3e8',
                cursor: 'pointer'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(0, 128, 96, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                  color: 'white',
                  transition: 'transform 0.2s, background 0.2s'
                }}>
                  <Icon source={PlayMajor} color="base" backdrop={false} />
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '20px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  padding: '8px 15px',
                  borderRadius: '4px',
                  fontWeight: '500'
                }}>
                  2:45
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Step by Step Process */}
        <div style={{ marginTop: '40px' }}>
          <Heading>Step-by-Step Process</Heading>
          <Text variant="bodyMd" as="p" color="subdued" style={{ marginBottom: '30px' }}>
            Follow these simple steps to optimize your product listings
          </Text>
          
          {workflowSteps.map((step, index) => (
            <Card key={index} sectioned style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#f4f6f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  flexShrink: 0
                }}>
                  {step.icon}
                </div>
                
                <div style={{ flexGrow: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <div style={{
                      backgroundColor: '#008060',
                      color: 'white',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      {step.number}
                    </div>
                    <Heading element="h2">{step.title}</Heading>
                  </div>
                  
                  <Text variant="bodyLg" as="p" style={{ marginBottom: '15px' }}>
                    {step.description}
                  </Text>
                  
                  <Stack vertical spacing="tight">
                    {step.details.map((detail, i) => (
                      <Stack alignment="center" key={i}>
                        <div style={{ color: '#008060' }}>
                          <Icon source={CircleTickMajor} />
                        </div>
                        <Text variant="bodyMd" as="span">{detail}</Text>
                      </Stack>
                    ))}
                  </Stack>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* User Workflow Diagram */}
        <Card sectioned style={{ marginTop: '30px' }}>
          <div style={{ textAlign: 'center', padding: '10px 0 30px' }}>
            <Heading>Workflow Diagram</Heading>
            <Text variant="bodyMd" as="p" color="subdued" style={{ marginBottom: '30px' }}>
              See how the entire process flows from product selection to pushing changes
            </Text>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              maxWidth: '900px',
              margin: '0 auto',
              overflowX: 'auto',
              padding: '10px 0'
            }}>
              {workflowSteps.map((step, index) => (
                <React.Fragment key={index}>
                  <div style={{ 
                    textAlign: 'center', 
                    minWidth: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      backgroundColor: '#f4f6f8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '30px',
                      marginBottom: '15px',
                      position: 'relative',
                      zIndex: 2
                    }}>
                      {step.icon}
                      <div style={{ 
                        position: 'absolute', 
                        top: '-5px', 
                        right: '-5px', 
                        backgroundColor: '#008060', 
                        color: 'white',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}>{step.number}</div>
                    </div>
                    <Text variant="headingSm" as="h3">{step.title}</Text>
                  </div>
                  
                  {index < workflowSteps.length - 1 && (
                    <div style={{ 
                      flex: '1', 
                      height: '2px', 
                      backgroundColor: '#dfe3e8',
                      margin: '0 10px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <div style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        right: '0', 
                        transform: 'translate(50%, -50%) rotate(45deg)', 
                        width: '10px', 
                        height: '10px', 
                        borderTop: '2px solid #dfe3e8', 
                        borderRight: '2px solid #dfe3e8'
                      }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Card>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '40px', 
          padding: '40px 20px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f9f9ff 0%, #f0f9ff 100%)',
          borderRadius: '8px'
        }}>
          <Heading>Ready to Optimize Your Shopify Store?</Heading>
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '650px' }}>
            Start creating better product listings with our AI-powered tools
          </Text>
          <Button size="large" primary onClick={handleLoginClick}>
            Get Started Free
          </Button>
        </div>
      </Page>
    </Frame>
  );
};

export default HowItWorks;
